import { AlertTriangle } from "lucide-react";
import {
  ALPHANUMERIC,
  AUTOCOMPLETE_ADDRESS,
  AUTOCOMPLETE_ZIPCODE,
  CARDRADIO,
  CHECKBOX_TYPES,
  CONTAINER,
  CURRENCY,
  DATE_TYPES,
  DROPDOWN_TYPES,
  DUMMY,
  EIN,
  GROUP_CHECKBOX,
  HORIZONTAL_LINE,
  MULTILINE_TEXTFIELD,
  NUMBER,
  PERCENT,
  PHONE_NUMBER,
  RADIO,
  SSN,
  STATE_AUTOCOMPLETE,
  VOID,
  PASSWORD
} from "../../../utils/InputFieldConfig";
import { CheckboxField } from "../checkbox-field/checkbox-field";
import { CurrencyField } from "../currency-field/currency-field";
import { DatePicker } from "../date-picker/date-picker";
import { DropdownField } from "../dropdown-field/dropdown-field";
import { EINField } from "../ein-field/ein-field";
import { TextareaField } from "../multiline-text-field/multiline-text-field";
import { NumberField } from "../number-field/number-field";
import { PercentField } from "../percent-field/percent-field";
import { PhoneField } from "../phone-number/phone-number";
import { RadioField } from "../radio-field/radio-field";
import { CardRadioField } from "../card-radio/card-radio-field";
import { SSNInput } from "../ssn-input/ssn-input";
import { TextField } from "../text-field/text-field";
import { ContainerInput } from "../container-input/container-input";
import { CheckboxGroupField } from "../checkbox-group-field/checkbox-group-field";
import { AutocompleteAddress } from "../autocomplete-address-field/autocomplete-address-field";
import { AutocompleteZipcode } from "../autocomplete-zipcode-field/autocomplete-zipcode-field";
import { StateAutocomplete } from "../state-autocomplete-field/state-autocomplete-field";
import { HorizontalLineField } from "../horizontal-line/horizontal-line-field";
import { PasswordValidationField } from "../password-field/password-validation-field";

export function InputFields({
  fieldValue,
  item,
  error,
  onFieldChange,
  onFieldBlur = () => {},
  fieldValues = [],
  componentContext = {},
  currentPiiInfoFields,
  handleFieldLevelHandler = () => {},
  supplementAddressData = {},
  disabled,
  autoFocus,
  customClasses,
}) {
  const renderField = () => {
    switch (item.type.toLowerCase()) {
      case DROPDOWN_TYPES:
        return (
          <DropdownField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onValueChange={(value) =>
              onFieldChange(item.pathToValue, value, item.type, item)
            }
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case DATE_TYPES:
        return (
          <DatePicker
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onFieldChange={onFieldChange}
            className={customClasses?.inputValue}
          />
        );

      case CHECKBOX_TYPES:
        return (
          <CheckboxField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onCheckedChange={(checked) =>
              onFieldChange(item.pathToValue, checked, item.type, item)
            }
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case RADIO:
        return (
          <RadioField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onValueChange={(value) =>
              onFieldChange(item.pathToValue, value, item.type, item)
            }
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case CARDRADIO:
        return (
          <CardRadioField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onValueChange={(value) =>
              onFieldChange(item.pathToValue, value, item.type, item)
            }
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case SSN:
        return (
          <SSNInput
            item={item}
            value={fieldValue || ""}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onChange={(event, value) =>
              onFieldChange(item.pathToValue, value, item.type, item)
            }
            onBlur={(event, value) =>
              onFieldBlur(item.pathToValue, value, item.type, item)
            }
            showPassword={currentPiiInfoFields?.ssn}
            handleFieldLevelHandler={handleFieldLevelHandler}
            currentPiiInfoFields={currentPiiInfoFields}
            className={customClasses?.inputValue}
          />
        );

      case EIN:
        return (
          <EINField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );

      case CURRENCY:
        return (
          <CurrencyField
            item={item}
            value={fieldValue || ""}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );

      case PERCENT:
        return (
          <PercentField
            item={item}
            value={fieldValue || ""}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            onBlur={(e) =>
              onFieldBlur(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );

      case PHONE_NUMBER:
        return (
          <PhoneField
            item={item}
            value={fieldValue || ""}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onFieldChange={onFieldChange}
            className={customClasses?.inputValue}
          />
        );

      case MULTILINE_TEXTFIELD:
        return (
          <TextareaField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            onBlur={(e) =>
              onFieldBlur(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );

      case NUMBER:
        return (
          <NumberField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            autoFocus={autoFocus}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            onBlur={(e) =>
              onFieldBlur(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );

        case PASSWORD:
          return (
            <PasswordValidationField
              item={item}
              value={fieldValue}
              error={error.isError}
              helperText={error.message}
              disabled={disabled}
              autoFocus={autoFocus}
              showValidation={item.showValidation}
              onChange={(e) =>
                onFieldChange(item.pathToValue, e.target.value, item.type, item)
              }
              onBlur={(e) =>
                onFieldBlur(item.pathToValue, e.target.value, item.type, item)
              }
              className={customClasses?.inputValue}
            />
          );

      case ALPHANUMERIC:
        return (
          <TextField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            autoFocus={autoFocus}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            onBlur={(e) =>
              onFieldBlur(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );

      case GROUP_CHECKBOX:
        return (
          <CheckboxGroupField
            item={item}
            value={fieldValue}
            error={error?.isError}
            helperText={error?.message}
            disabled={disabled}
            onFieldChange={onFieldChange}
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case AUTOCOMPLETE_ADDRESS:
        return (
          <AutocompleteAddress
            item={item}
            value={fieldValue}
            error={error?.isError}
            helperText={error?.message}
            disabled={disabled}
            onFieldChange={onFieldChange}
            supplementAddressData={supplementAddressData}
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case AUTOCOMPLETE_ZIPCODE:
        return (
          <AutocompleteZipcode
            item={item}
            value={fieldValue}
            error={error?.isError}
            helperText={error?.message}
            disabled={disabled}
            onFieldChange={onFieldChange}
            supplementAddressData={supplementAddressData}
            className={customClasses?.inputValue}
            labelClassName={customClasses?.labelClassName}
          />
        );

      case STATE_AUTOCOMPLETE:
        return (
          <StateAutocomplete
            item={item}
            fieldValue={fieldValue}
            onFieldChange={onFieldChange}
            error={error?.isError}
            helperText={error?.message}
            disabled={disabled}
            className={customClasses?.inputValue}
          />
        );

      case CONTAINER:
        return (
          <ContainerInput
            fields={item.fields}
            componentContext={componentContext}
            onFieldChange={onFieldChange}
            fieldValues={fieldValues[item.uniqueField]}
            disabled={item.disabled || disabled}
            className={customClasses}
          />
        );

      case HORIZONTAL_LINE:
        return (
          <HorizontalLineField
            item={item}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            customClasses={customClasses}
            className={customClasses?.inputValue}
          />
        );

      case VOID:
        return <div />;

      case DUMMY:
        return null;

      default:
        return (
          <TextField
            item={item}
            value={fieldValue}
            error={error.isError}
            helperText={error.message}
            disabled={disabled}
            autoFocus={autoFocus}
            onChange={(e) =>
              onFieldChange(item.pathToValue, e.target.value, item.type, item)
            }
            onBlur={(e) =>
              onFieldBlur(item.pathToValue, e.target.value, item.type, item)
            }
            className={customClasses?.inputValue}
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      {renderField()}
      {error?.isError && error?.message && (
        <div className="flex items-center mt-1 text-red-600 text-xs">
          <AlertTriangle className="h-3 w-3 mr-1" />
          <span className="mt-[1px]">{error?.message}</span>
        </div>
      )}
    </div>
  );
}
